import React, { PureComponent, Fragment } from "react";
import PropTypes from "prop-types";
import { CustomInput } from "reactstrap";
import memoize from "memoize-one";
import classNames from "classnames";

import isEqual from "lodash.isequal";
import { withLoading, DeviceTableRow as Device, SaveCancelBar, VirtualizedDivTable } from "./";
import { withAlert } from "./Alert";

const getItemKey = (index, data) => `list_client#${data.context}_${data.devicesGroups[index].isGroup ? "group" : "device"}#${data.devicesGroups[index].id}#${index}`;

const isInGroup = (device, group) => {
  return group.id // the group exists
    ? device.group?.id === group.id // all in the group
    : !device.group; // all without group
};

class GroupDevice extends PureComponent {
  render() {
    const { data, index, style } = this.props;
    const { columns, autoColumnWidth, devicesGroups, selectElement, selectGroup, selectedDevices, filterValue, canAssignDevices, canAccessDeveloperSettings, canAccessSimCards } = data;
    
    if (!devicesGroups || !devicesGroups.length) {
      return null;
    }

    const item = devicesGroups[index];
    const isGroupSelected = devicesGroups[index].isGroup ? data.isGroupSelected(devicesGroups[index]) : false;

    return (
      <div key={item.isGroup ? `g#${item.id}` : item.id} style={style}>
        {item.isGroup
          ? (
            <div className={classNames({ "div-table-odd-row": index % 2 === 0 }, "div-table-row")}>
              <div className="div-table-cell" style={{ width: !columns[0].width || columns[0].width === "auto" ? autoColumnWidth : columns[0].width }}>
                {filterValue
                  ? null
                  : <CustomInput type="checkbox" id={`group#${item.id}`} checked={isGroupSelected} onChange={selectGroup(devicesGroups[index], !isGroupSelected)} disabled={!canAssignDevices} />
                }
              </div>
              <div className="div-table-cell" style={{ width: columns.slice(1).reduce((a, col) => a += !col.width || col.width === "auto" ? autoColumnWidth : col.width, 0) }}>
                <strong style={{ fontSize: "1rem" }}>{item.name || "Bez nazwy"}</strong>
              </div>
            </div>
          )
          : (
            <Device
              className={classNames({ "div-table-odd-row": index % 2 === 0 })}
              showCheckboxes
              key={item.id}
              device={item}
              hasReassignPermission={canAssignDevices}
              canAccessDeveloperSettings={canAccessDeveloperSettings}
              selected={!!selectedDevices[item.id]}
              selectElement={selectElement}
              canAccessSimCards={canAccessSimCards}
              columns={columns}
              autoColumnWidth={autoColumnWidth}
            />
          )}
      </div>
    );
  }
}

class DevicesPickerTable extends PureComponent {
  handleResponse = r => {
    if (r.result === 0) {
      this.props.showAlert("Zmiany zostały zapisane!", "success", this.props.refreshDevicesList);
    }
    else {
      this.handleError();
    }
  };

  handleError = () => {
    this.props.showAlert("Zmiany nie zostały zapisane!", "danger");
  };

  cancel = selectElements => () => {
    selectElements(this.props.devices.reduce((a, x) => {
      if (x.selected) {
        a.push(x.id);
      }

      return a;
    }, []), true, true);
  };

  save = () => {
    this.props.save() // make sure the save method is returning a promise
      .then(this.handleResponse)
      .catch(this.handleError);
  };

  selectGroup = (group, selected) => () => {
    const devicesIds = this.props.devicesGroups.filter(x => !x.isGroup && isInGroup(x, group)).map(x => x.id);

    this.props.selectElements(devicesIds, selected);
  };

  isGroupSelected = group => {
    for (let device of this.props.devicesGroups) {
      if (!device.isGroup && isInGroup(device, group) && !this.props.selectedDevices[device.id]) {
        return false;
      }
    }

    return true;
  };

  createItemData = memoize((context, devicesGroups, selectElement, selectedDevices, filterValue, canAssignDevices, canAccessDeveloperSettings, canAccessSimCards) => ({
    context,
    devicesGroups,
    selectElement,
    selectedDevices,
    canAssignDevices,
    canAccessDeveloperSettings,
    canAccessSimCards,
    isGroupSelected: this.isGroupSelected,
    selectGroup: this.selectGroup,
  }), isEqual);

  selectAll = () => {
    this.props.selectElements(this.props.devicesGroups.filter(x => !x.isGroup).map(x => x.id), true);
  };

  deselectAll = () => {
    this.props.selectElements(this.props.devicesGroups.filter(x => !x.isGroup).map(x => x.id), false);
  };

  areAllSelected = () => {
    return this.props.devicesGroups.filter(x => !x.isGroup).every(x => !!this.props.selectedDevices[x.id]);
  };

  render() {
    const {
      isFetching,
      devicesGroups,
      className,
      selectedDevices,
      selectElement,
      selectElements,
      selected,
      deselected,
      isDirty,
      canAssignDevices,
      canAccessDeveloperSettings,
      canAccessSimCards,
      context,
      filterValue,
    } = this.props;

    const { selectAll, deselectAll, areAllSelected } = this;

    if (isFetching) {
      return <div className={className} />;
    }
    
    const allSelected = areAllSelected();
    const itemData = this.createItemData(context, devicesGroups, selectElement, selectedDevices, filterValue, canAssignDevices, canAccessDeveloperSettings, canAccessSimCards);

    const columns = [
      { id: "selected", value: <CustomInput type="checkbox" id="selectAllDevices" checked={allSelected} onChange={allSelected ? deselectAll : selectAll} disabled={!canAssignDevices} />, width: 50 },
      { id: "name", value: "Nazwa", width: 150 },
      { id: "makeAndModel", value: "Marka i model", width: "auto" },
      { id: "vrn", value: "Nr rejestracyjny", width: "auto" },
      { id: "vehicleNumber", value: "Nr boczny", width: 100 },
      { id: "serialNumber", value: "Nr seryjny", width: 120 },
      { id: "imei", value: "IMEI", width: 130 },
      { id: "ccid", value: "CCID", width: 180 },
      { id: "simCardPhoneNumber", value: "Nr tel.", width: 100 }
    ];

    if (canAccessDeveloperSettings) {
      columns.push({ id: "id", value: "Id", width: 65 });
    }

    return (
      <Fragment>
        <div style={{ height: "calc(100% - 3.5rem)" }}>
          <VirtualizedDivTable className={className} columns={columns} items={devicesGroups} itemSize={49}
            getItemKey={getItemKey} itemData={itemData} itemRenderer={GroupDevice}
          />
        </div>

        <SaveCancelBar isDirty={isDirty} cancel={this.cancel(selectElements)} save={this.save}>
          <Fragment>
            <span className="mr-2">Dodane: {selected.length}</span>
            <span className="mr-2">Usunięte: {deselected.length}</span>
          </Fragment>
        </SaveCancelBar>
      </Fragment>
    );
  }
}

DevicesPickerTable.propTypes = {
  refreshDevicesList: PropTypes.func.isRequired,
  selectElement: PropTypes.func.isRequired,
  selectElements: PropTypes.func.isRequired,
  canAssignDevices: PropTypes.bool.isRequired,
  save: PropTypes.func.isRequired,
  context: PropTypes.string.isRequired,
  devicesGroups: PropTypes.array,
  devices: PropTypes.array,
  filterValue: PropTypes.string,
};

export default withAlert(withLoading(DevicesPickerTable));
